import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';

/**
 * Super-admin client. Hits `/system/*` (outside `/api/v1`) per Section 13B.1.
 * Sends the same Bearer JWT as the regular client — the server enforces the
 * @domain + allowlist gate.
 */
const systemClient = axios.create({
  baseURL: '/system',
  headers: { 'Content-Type': 'application/json' },
});

systemClient.interceptors.request.use((c) => {
  const token = useAuthStore.getState().token;
  if (token) c.headers.Authorization = `Bearer ${token}`;
  return c;
});

export interface OrganisationUsage {
  org_id: string;
  /** Company name as set in the org's Brand kit. Null until configured —
   *  the UI falls back to org_id. */
  company_name: string | null;
  user_count: number;
  active_campaigns: number;
  total_campaigns: number;
  connected_mailboxes: number;
  active_mailboxes: number;
  sends_today: number;
  last_active_at: string | null;
}

export interface AccountRow {
  jt_user_id: string;
  org_id: string;
  email: string;
  name: string;
  role: string;
  last_synced_at: string;
}

export interface SystemHealth {
  organisations: number;
  users: number;
  campaigns: number;
  active_mailboxes: number;
  sends_today: number;
}

export interface TakeoverResponse {
  token: string;
  target: AccountRow;
}

export function useOrganisations() {
  return useQuery<OrganisationUsage[]>({
    queryKey: ['system', 'organisations'],
    queryFn: async () => {
      const { data } = await systemClient.get<{ data: OrganisationUsage[] }>('/organisations');
      return data.data;
    },
  });
}

export function useOrganisation(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['system', 'organisations', orgId],
    queryFn: async () => {
      const { data } = await systemClient.get<{ data: OrganisationUsage & { accounts: AccountRow[] } }>(
        `/organisations/${orgId}`
      );
      return data.data;
    },
    enabled: !!orgId,
  });
}

export function useAccounts(orgId?: string) {
  return useQuery<AccountRow[]>({
    queryKey: ['system', 'accounts', orgId ?? null],
    queryFn: async () => {
      const { data } = await systemClient.get<{ data: AccountRow[] }>('/accounts', {
        params: orgId ? { org_id: orgId } : undefined,
      });
      return data.data;
    },
  });
}

export function useSystemHealth() {
  return useQuery<SystemHealth>({
    queryKey: ['system', 'health'],
    queryFn: async () => {
      const { data } = await systemClient.get<{ data: SystemHealth }>('/health');
      return data.data;
    },
  });
}

export function useInitiateTakeover() {
  return useMutation({
    mutationFn: async (target_user_id: string) => {
      const { data } = await systemClient.post<{ data: TakeoverResponse }>(
        '/takeover/initiate',
        { target_user_id }
      );
      return data.data;
    },
  });
}

export interface UsageSnapshotRow {
  snapshot_date: string;
  active_campaigns: number;
  total_sends: number;
  marketing_contacts_count: number;
  connected_mailboxes: number;
  active_users: number;
}

export function useOrganisationTrend(orgId: string | null | undefined, days = 30) {
  return useQuery<UsageSnapshotRow[]>({
    queryKey: ['system', 'organisations', orgId, 'trend', days],
    queryFn: async () => {
      const { data } = await systemClient.get<{ data: UsageSnapshotRow[] }>(
        `/organisations/${orgId}/trend`,
        { params: { days } }
      );
      return data.data;
    },
    enabled: !!orgId,
  });
}

export function useRunUsageSnapshots() {
  return useMutation({
    mutationFn: async (opts: { sync?: boolean } = {}) => {
      const { data } = await systemClient.post<{ data: { mode: 'sync' | 'enqueued'; orgs?: number; failed?: string[] } }>(
        '/usage-snapshots/run',
        null,
        { params: opts.sync ? { sync: 'true' } : undefined }
      );
      return data.data;
    },
  });
}

/**
 * Triggers a CSV download in the browser. Returns nothing — the browser
 * handles the file save dialog.
 */
export async function downloadOrganisationsCsv(token: string | null): Promise<void> {
  const res = await fetch('/system/organisations.csv', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`CSV download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `engage-organisations-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
